import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import type { KpiT, MemberDetailT, TeamDashboardPayloadT, TeamDrillPayloadT, TeamMembersPayloadT } from "./types";

export type SellerDrawerSeedT = {
  id: number;
  fullName: string;
  currentRank: number;
  avatarUrls: MemberDetailT["avatarUrls"];
  level?: number;
  sponsorName?: string;
  enrolledAt?: string;
  recommendationId?: number;
};

export type SellerDrawerStateT = {
  open: boolean;
  seed: SellerDrawerSeedT | null;
};

export type TeamIntelligenceReduxStateT = {
  teamMembers: TeamMembersPayloadT;
  teamDashboard: TeamDashboardPayloadT;
  teamDrill: TeamDrillPayloadT;
  sellerDrawer: SellerDrawerStateT;
  memberDetail: MemberDetailT;
};

const TREND_KPI: KpiT = { value: 0, prior: 0, delta: 0, sparkline: Array(15).fill(0) };
const PULSE_KPI: KpiT = { value: 0, prior: 0, delta: 0, sparkline: null };
const COUNT_KPI: KpiT = { value: 0, prior: null, delta: null, sparkline: null };

const initialState: TeamIntelligenceReduxStateT = {
  teamMembers: {
    members: [],
    averages: { prv: "0", brv: "0", activity: 0 },
    kpis: { total: 0, activeRate: 0, avgRank: "0", atRiskCount: 0, coachingQueue: 0 },
    paging: { itemCount: 0, pageNumber: 1, pageSize: 20, hasMore: false },
  },
  teamDashboard: {
    kpis: {
      teamBrv: TREND_KPI,
      newInstalls: TREND_KPI,
      newContracts: TREND_KPI,
      newRecruits: TREND_KPI,
      rankAdvancements: TREND_KPI,
      totalActiveTeam: COUNT_KPI,
      tier1Setters: COUNT_KPI,
      tier2Sellers: COUNT_KPI,
      tier3Mentors: COUNT_KPI,
      enterprises: COUNT_KPI,
      newLeads: PULSE_KPI,
      newProposals: PULSE_KPI,
      proposalToContract: PULSE_KPI,
      activeSellers: PULSE_KPI,
      avgPrvPerActive: PULSE_KPI,
      orgGrowth: PULSE_KPI,
    },
  },
  teamDrill: {
    brvContributions: [],
    teamComposition: [],
    recentInstalls: { totalContractValue: 0, rows: [] },
    activeContracts: { stalled: [], active: [] },
    newRecruits: { stalled: [], onTrack: [] },
    rankUps: { recent: [], approaching: [] },
  },
  sellerDrawer: {
    open: false,
    seed: null,
  },
  memberDetail: {
    id: 0,
    fullName: "",
    currentRank: 0,
    level: 0,
    sponsor: { id: 0, fullName: "" },
    enrolledAt: "",
    avatarUrls: {},
    overview: {
      prv: 0,
      brv: 0,
      activeQls: 0,
      newLeads: 0,
      proposals: 0,
      contracts: 0,
      installs: 0,
    },
    rankUp: {
      nextRank: 0,
      requirements: [],
      earnings: { ldbPayout: 0 },
    },
    pipeline: {
      stalled: [],
      active: [],
    },
    team: {
      downline: { sellerCount: 0, r2PlusCount: 0, brv30d: 0 },
      sponsees: [],
    },
    recommendations: [],
  },
};

export const teamIntelligenceSlice = createSlice({
  name: "teamIntelligence",
  initialState,
  reducers: {
    openSellerDrawer: (state, { payload }: PayloadAction<SellerDrawerSeedT>) => {
      state.sellerDrawer.open = true;
      state.sellerDrawer.seed = payload;
    },
    closeSellerDrawer: (state) => {
      state.sellerDrawer.open = false;
      state.sellerDrawer.seed = null;
    },
  },
  selectors: {
    selectSellerDrawer: (state) => state.sellerDrawer,
  },
});

export const { openSellerDrawer, closeSellerDrawer } = teamIntelligenceSlice.actions;
export const { selectSellerDrawer } = teamIntelligenceSlice.selectors;

export const {
  teamMembers: teamMembersInitialState,
  teamDashboard: teamDashboardInitialState,
  teamDrill: teamDrillInitialState,
  memberDetail: memberDetailInitialState,
} = initialState;
